import { Autocomplete, Box, Stack, TextField, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { resolveDisplayText } from "modules/record/shared/services/recordMetadataLabels";
import UserSelect from "./UserSelect";
import DataPicker from "./DataPicker";
import { QueueSelection } from "./fluss-builder-types";
import { FlussMetadataOption, referenceId } from "./node-properties/fluss-node-panel-data";

const RECORD_PERSON = "__record_person__";
const FIXED_USER_KEY = "__fixed_user__";
// Onda 11 D10 supersedes PRIM-023: the inbox matches every effective active role.
const qualifyingRoles = (roles: FlussMetadataOption[]) =>
    roles.filter((role) => role.active !== false);

// One row per (group, role) pair — round 6 default, confirmed with the architect: a group with
// more than one qualifying role gets one row per pair, not a group-then-role two-step picker.
// Deliberately NOT reusing `queues` (queueOptions' pre-joined list in FlussNodePropertiesPanel.tsx)
// — that one `.find()`s the FIRST membership per role, so a role linked to multiple groups only
// ever showed one. Joining here from the raw roles/groups/memberships instead.
const groupRoleOptions = (
    roles: FlussMetadataOption[],
    groups: FlussMetadataOption[],
    memberships: FlussMetadataOption[]
) => {
    const qualifying = qualifyingRoles(roles);
    const linked = memberships.flatMap((membership) => {
        const role = qualifying.find((candidate) => candidate.id === referenceId(membership.sys_role_id));
        if (!role) return [];
        const group = groups.find((candidate) => candidate.id === referenceId(membership.sys_group_id));
        if (!group || group.active === false) return [];
        return [{
            groupName: group.label || group.name,
            groupCode: group.name,
            roleName: role.name,
            roleLabel: role.label || role.name,
        }];
    });
    const linkedGroupIds = new Set(memberships.filter((membership) => qualifying.some((role) => role.id === referenceId(membership.sys_role_id)))
        .map((membership) => referenceId(membership.sys_group_id)));
    return [...linked, ...groups.filter((group) => group.active !== false && !linkedGroupIds.has(group.id))
        .map((group) => ({ groupName: group.label || group.name, groupCode: group.name, roleName: "", roleLabel: "" }))];
};

interface QueueOptionItem {
    key: string;
    header: string;
    label: string;
    secondary: string;
    search: string;
    selection: QueueSelection;
    disabled?: boolean;
}

export interface QueueSelectProps {
    value: QueueSelection;
    onChange: (value: QueueSelection) => void;
    // PRIM-023 (round 6): raw roles/groups/memberships behind the Roles/Grupos accordions —
    // replaces the old pre-joined `queues: QueueOption[]` prop (queueOptions() in
    // FlussNodePropertiesPanel.tsx), which only ever matched a role to its FIRST group membership.
    roles: FlussMetadataOption[];
    groups: FlussMetadataOption[];
    groupRoles: FlussMetadataOption[];
    lockedMode?: "queue" | "record_person" | "fixed_user";
    disabled?: boolean;
}

export const QueueSelect = ({
    value,
    onChange,
    roles = [],
    groups = [],
    groupRoles = [],
    lockedMode,
    disabled = false,
}: QueueSelectProps) => {
    const { t } = useTranslation();
    // Onda 11.1 E6: Autocomplete with search by label and technical name. Roles and groups are
    // shown by their translated label (never the raw `record.sys_groups.label.<uuid>` key).
    const text = (raw: string) => String(resolveDisplayText(t, raw));
    const headers = {
        roles: String(t("fluss.builder.queue.roles", { defaultValue: "Roles" })),
        groups: String(t("fluss.builder.queue.groups", { defaultValue: "Groups" })),
        record: String(t("fluss.builder.queue.record", { defaultValue: "Record" })),
    };
    const options = useMemo<QueueOptionItem[]>(() => {
        const roleItems = qualifyingRoles(roles).map((role): QueueOptionItem => {
            const label = text(role.label || role.name);
            return {
                key: `role:${role.name}`, header: headers.roles, label, secondary: "",
                search: `${label} ${role.name}`, selection: { mode: "queue", roleName: role.name },
            };
        });
        const groupItems = groupRoleOptions(roles, groups, groupRoles).map((option, index): QueueOptionItem => {
            const label = text(option.groupName);
            const secondary = option.roleLabel ? text(option.roleLabel) : "";
            return {
                key: `group:${option.groupCode}:${option.roleName}:${index}`, header: headers.groups, label, secondary,
                search: `${label} ${option.groupCode} ${secondary} ${option.roleName}`,
                selection: { mode: "queue", roleName: option.roleName }, disabled: !option.roleName,
            };
        });
        const recordItems: QueueOptionItem[] = lockedMode ? [] : [
            { key: RECORD_PERSON, header: headers.record, secondary: "",
                label: String(t("fluss.builder.queue.record_person", { defaultValue: "Person from record" })),
                search: "person record", selection: { mode: "record_person", source: undefined } },
            { key: FIXED_USER_KEY, header: headers.record, secondary: "",
                label: String(t("fluss.builder.queue.fixed_user", { defaultValue: "Fixed user" })),
                search: "fixed user", selection: { mode: "fixed_user", userId: "" } },
        ];
        return [...roleItems, ...groupItems, ...recordItems];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roles, groups, groupRoles, lockedMode, t]);

    const selected = value?.mode === "queue"
        ? options.find((option) => option.selection?.mode === "queue" && option.selection.roleName === value.roleName && !option.disabled) || null
        : value?.mode === "fixed_user"
            ? options.find((option) => option.key === FIXED_USER_KEY) || null
            : value
                ? options.find((option) => option.key === RECORD_PERSON) || null
                : null;

    return (
        <Stack spacing={1}>
            {lockedMode !== "record_person" && lockedMode !== "fixed_user" && (
            <Autocomplete<QueueOptionItem>
                size="small"
                fullWidth
                disabled={disabled}
                options={options}
                value={selected}
                groupBy={(option) => option.header}
                getOptionLabel={(option) => option.label}
                getOptionDisabled={(option) => Boolean(option.disabled)}
                isOptionEqualToValue={(option, current) => option.key === current.key}
                filterOptions={(items, state) => {
                    const needle = state.inputValue.trim().toLocaleLowerCase();
                    return needle ? items.filter((item) => item.search.toLocaleLowerCase().includes(needle)) : items;
                }}
                noOptionsText={String(t("fluss.builder.queue.no_results", { defaultValue: "No matching queue" }))}
                onChange={(_event, option) => option && onChange(option.selection)}
                renderOption={(props, option) => (
                    <li {...props} key={option.key}
                        title={option.disabled ? String(t("fluss.builder.queue.group_without_role", { defaultValue: "No active role linked to this group" })) : undefined}>
                        <Box>
                            <Typography variant="body2">{option.label}</Typography>
                            {option.secondary && <Typography variant="caption" color="text.secondary">{option.secondary}</Typography>}
                        </Box>
                    </li>
                )}
                renderInput={(params) => (
                    <TextField {...params}
                        placeholder={String(t("fluss.builder.queue.search", { defaultValue: "Search roles or groups" }))}
                        inputProps={{
                            ...params.inputProps,
                            "aria-label": String(t("fluss.builder.queue.label", { defaultValue: "Assignment queue" })),
                        }} />
                )}
            />
            )}

            {value?.mode === "record_person" && (
                <>
                    <DataPicker
                        value={value.source}
                        onChange={(source) => onChange({ mode: "record_person", source })}
                        referencedTable="sys_users"
                        allowFixed={false}
                        disabled={disabled}
                        ariaLabel={String(t("fluss.builder.queue.person_field", {
                            defaultValue: "Person field",
                        }))}
                    />
                </>
            )}

            {value?.mode === "fixed_user" && (
                <>
                    <UserSelect
                        size="small"
                        label={t("fluss.builder.queue.fixed_user", { defaultValue: "Fixed user" })}
                        value={value.userId || ""}
                        onChange={(userId) => onChange({ mode: "fixed_user", userId })}
                        disabled={disabled}
                    />
                    <Typography variant="caption" color="text.secondary">
                        {t("fluss.builder.queue.fixed_user_acl_hint", { defaultValue: "The list shows only users you can see." })}
                    </Typography>
                </>
            )}
        </Stack>
    );
};

export default QueueSelect;
